import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { PopupManager } from '../../../../_shared/managers/popup.manager';

export type PopupItemButton = Partial<{
    label: string;
    cta: () => void;
    hide: boolean;
    className: string;
}>;

export enum PopupType {
    Error,
    Info,
}

export type PopupItemCTA = Partial<{
    primary: PopupItemButton | boolean;
    secondary: PopupItemButton | boolean;
    close: PopupItemButton | boolean;
}>;

export interface PopupItemProps {
    type: PopupType;
    title?: string;
    icon?: string;
    closeOnClickOutside?: boolean;
    children?: JSX.Element | React.ReactNode;
    description?: string;
    ctas?: PopupItemCTA;
    maxWidth?: 'sm' | 'lg' | 'xl' | undefined;
    stylingProp?: string;
    dialogActionStyle?: string;
    close?: boolean;
}

const maxWidthMap: Record<string, string> = {
    sm: '520px',
    lg: '680px',
    xl: '860px',
};

const PopupItem: React.FC<PopupItemProps> = ({
    type,
    title,
    icon,
    description,
    maxWidth,
    ctas = {},
    closeOnClickOutside,
    children,
}) => {
    const primary = ctas.primary as PopupItemButton;
    const secondary = ctas.secondary as PopupItemButton;
    const dialogRef = useRef<HTMLDivElement>(null);

    const [visible, setVisible] = useState(false);
    const [closing, setClosing] = useState(false);

    const dialogMaxWidth = maxWidthMap[maxWidth || 'sm'] || '520px';

    const triggerClose = useCallback(() => {
        if (closing) return;
        setClosing(true);
        setTimeout(() => {
            PopupManager.getInstance().hide();
        }, 200);
    }, [closing]);

    // Trigger enter animation after mount
    useEffect(() => {
        const frame = requestAnimationFrame(() => setVisible(true));
        return () => cancelAnimationFrame(frame);
    }, []);

    // Escape key listener
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') triggerClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [triggerClose]);

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (closeOnClickOutside !== false && dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
            triggerClose();
        }
    };

    const handleClick =
        (callback?: () => void, hide = false) =>
        async () => {
            if (callback) await callback();
            if (hide) triggerClose();
        };

    const isEntering = visible && !closing;

    // Resolve icon: custom prop > type-based fallback
    const defaultIcon = type === PopupType.Error ? 'bi-exclamation-triangle-fill' : 'bi-info-circle-fill';
    const iconName = icon || defaultIcon;

    // Backdrop styles
    const backdropStyle: React.CSSProperties = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1050,
        opacity: isEntering ? 1 : 0,
        transition: closing ? 'opacity 0.2s ease-in' : 'opacity 0.25s ease-out',
    };

    // Dialog styles
    const dialogStyle: React.CSSProperties = {
        backgroundColor: '#fff',
        borderRadius: '14px',
        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
        border: '1px solid rgba(0, 0, 0, 0.06)',
        width: '90%',
        maxWidth: dialogMaxWidth,
        maxHeight: '90vh',
        overflow: 'visible',
        opacity: isEntering ? 1 : 0,
        transform: isEntering ? 'scale(1)' : 'scale(0.95)',
        transition: closing
            ? 'opacity 0.2s ease-in, transform 0.2s ease-in'
            : 'opacity 0.3s ease-out, transform 0.3s ease-out',
    };

    // Header styles — red background with white text
    const headerStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '1rem 1.25rem',
        background: 'linear-gradient(135deg, #ff4949, #e03e3e)',
        borderRadius: '14px 14px 0 0',
    };

    const titleAreaStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
    };

    const titleStyle: React.CSSProperties = {
        fontWeight: 700,
        fontSize: '1.1rem',
        color: '#fff',
        margin: 0,
    };

    const closeButtonStyle: React.CSSProperties = {
        background: 'rgba(255, 255, 255, 0.15)',
        border: 'none',
        cursor: 'pointer',
        padding: '0.3rem',
        color: '#fff',
        fontSize: '1.05rem',
        lineHeight: 1,
        borderRadius: '6px',
        transition: 'background 0.15s',
    };

    // Body styles
    const bodyStyle: React.CSSProperties = {
        padding: '1.25rem 1.5rem',
    };

    const descriptionStyle: React.CSSProperties = {
        color: '#555',
        whiteSpace: 'pre-line',
        margin: 0,
        fontSize: '0.95rem',
        lineHeight: 1.6,
    };

    // Footer styles
    const footerStyle: React.CSSProperties = {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: '0.65rem',
        padding: '0.85rem 1.25rem',
        borderTop: '1px solid rgba(0, 0, 0, 0.06)',
    };

    const primaryBtnStyle: React.CSSProperties = {
        backgroundColor: '#ff4949',
        color: '#fff',
        border: 'none',
        borderRadius: '50px',
        padding: '0.5rem 1.5rem',
        fontWeight: 600,
        fontSize: '0.9rem',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease, transform 0.1s ease',
    };

    const secondaryBtnStyle: React.CSSProperties = {
        backgroundColor: 'transparent',
        color: '#ff4949',
        border: '1.5px solid #ff4949',
        borderRadius: '50px',
        padding: '0.5rem 1.5rem',
        fontWeight: 600,
        fontSize: '0.9rem',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease, transform 0.1s ease',
    };

    return ReactDOM.createPortal(
        <div style={backdropStyle} onMouseDown={handleBackdropClick}>
            <div ref={dialogRef} style={dialogStyle} role="dialog" aria-modal="true" aria-labelledby="popup-title">
                {/* Header */}
                <div style={headerStyle}>
                    <div style={titleAreaStyle}>
                        <i className={`bi ${iconName}`} style={{ color: '#fff', fontSize: '1.2rem' }} />
                        <h5 id="popup-title" style={titleStyle}>{title}</h5>
                    </div>
                    <button
                        style={closeButtonStyle}
                        onClick={triggerClose}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                        }}
                        aria-label="Close"
                    >
                        <i className="bi bi-x-lg" />
                    </button>
                </div>

                {/* Body */}
                {description && (
                    <div style={bodyStyle}>
                        <p style={descriptionStyle}>{description}</p>
                    </div>
                )}
                {children && (
                    <div style={{
                        ...bodyStyle,
                        paddingTop: description ? '0.25rem' : '1.75rem',
                        paddingBottom: '1.75rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        <div style={{ width: '100%' }}>{children}</div>
                    </div>
                )}

                {/* Footer */}
                <div style={footerStyle}>
                    {secondary && (
                        <button
                            style={secondaryBtnStyle}
                            onClick={handleClick(secondary.cta, secondary.hide)}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(255, 73, 73, 0.06)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                        >
                            {secondary.label || 'Cancel'}
                        </button>
                    )}
                    <button
                        style={primaryBtnStyle}
                        onClick={handleClick(primary?.cta, primary?.hide)}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#e63e3e';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#ff4949';
                        }}
                    >
                        {primary?.label || 'OK'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default PopupItem;
